import { DEFAULT_SAMPLE_RATE, mix, sequence, synthTone } from './waveforms';

export const SFX = [
  'edgeSelect',
  'edgeDeselect',
  'invalidMove',
  'levelComplete',
  'starEarned',
  'hintReveal',
  'buttonTap',
] as const;

export type Sfx = (typeof SFX)[number];

function note(
  frequency: number,
  seconds: number,
  sampleRate: number,
  amplitude = 0.4,
): Float32Array {
  return synthTone({
    frequency,
    durationSeconds: seconds,
    sampleRate,
    amplitude,
    waveform: 'sine',
    attack: 0.004,
    release: 0.05,
  });
}

const C5 = 523.25;
const E5 = 659.25;
const G5 = 783.99;
const C6 = 1046.5;
const E6 = 1318.51;
const E7 = 2637.02;

const BUILDERS: Record<Sfx, (sampleRate: number) => Float32Array> = {
  edgeSelect: (sampleRate) =>
    synthTone({
      frequency: 880,
      durationSeconds: 0.07,
      sampleRate,
      amplitude: 0.45,
      waveform: 'triangle',
      release: 0.05,
    }),

  edgeDeselect: (sampleRate) =>
    synthTone({
      frequency: 440,
      durationSeconds: 0.07,
      sampleRate,
      amplitude: 0.4,
      waveform: 'triangle',
      release: 0.05,
    }),

  invalidMove: (sampleRate) =>
    mix([
      synthTone({
        frequency: 150,
        durationSeconds: 0.18,
        sampleRate,
        amplitude: 0.4,
        waveform: 'square',
        release: 0.08,
      }),
      synthTone({
        frequency: 157,
        durationSeconds: 0.18,
        sampleRate,
        amplitude: 0.3,
        waveform: 'square',
        release: 0.08,
      }),
    ]),

  levelComplete: (sampleRate) =>
    sequence([
      note(C5, 0.1, sampleRate),
      note(E5, 0.1, sampleRate),
      note(G5, 0.1, sampleRate),
      note(C6, 0.2, sampleRate),
    ]),

  starEarned: (sampleRate) =>
    mix([
      synthTone({
        frequency: E6,
        durationSeconds: 0.22,
        sampleRate,
        amplitude: 0.4,
        waveform: 'sine',
        release: 0.16,
      }),
      synthTone({
        frequency: E7,
        durationSeconds: 0.16,
        sampleRate,
        amplitude: 0.15,
        waveform: 'sine',
        release: 0.12,
      }),
    ]),

  hintReveal: (sampleRate) =>
    sequence([note(G5, 0.09, sampleRate, 0.32), note(C6, 0.13, sampleRate, 0.32)]),

  buttonTap: (sampleRate) =>
    synthTone({
      frequency: 660,
      durationSeconds: 0.05,
      sampleRate,
      amplitude: 0.35,
      waveform: 'sine',
      release: 0.04,
    }),
};

const cache = new Map<Sfx, Float32Array>();

export function samplesFor(sfx: Sfx, sampleRate = DEFAULT_SAMPLE_RATE): Float32Array {
  if (sampleRate !== DEFAULT_SAMPLE_RATE) return BUILDERS[sfx](sampleRate);

  const cached = cache.get(sfx);
  if (cached) return cached;

  const built = BUILDERS[sfx](sampleRate);
  cache.set(sfx, built);
  return built;
}
